/**
 * SOLID Review
 * S — Strong. One concern: whether the 3D scene draws on WebGL or falls back to the 2D cup — the context lost, and its recovery when
 *     the tab comes back (a fresh canvas first, a reload if the context is lost again at once).
 * O — Good. Another recovery step is one more line of `recover`.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The canvas's creation, the snapshot, the runtime.
 * D — Strong. The scene's runtime, the platform's visibility, timers and reload, and whether WebGL was found.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IConfigDepViewPlatform } from './platform/IConfigDepViewPlatform';
import type { SceneRuntime } from './SceneRuntime';

/** How long a remounted canvas must hold its context before the recovery trusts it. */
const REMOUNT_GRACE_MS = 1_500;

export interface IStateSceneCanvas {
  /** WebGL draws the scene; when false the 2D cup does. */
  readonly webglOk: boolean;
  /** `?gldebug`: the runtime's counters in the debug overlay, the probe on the canvas. */
  readonly gldebug: boolean;
}

/** The canvas's WebGL context, as the scene's creation handler is handed it. */
export interface IEntityCanvasContext {
  readonly canvas: { addEventListener(type: 'webglcontextlost', listener: (event: Event) => void, useCapture: boolean): void };
}

export interface IConfigDepSceneCanvas {
  readonly runtime: SceneRuntime;
  readonly platform: Pick<IConfigDepViewPlatform, 'visibility' | 'timers' | 'reload' | 'address'>;
  /** The browser's WebGL2 at load (`webglSupport.ts`): false — no WebGL2, or `?force2d` — and nothing is ever recovered. */
  readonly webglAvailable: boolean;
}

const LOST: Partial<IStateSceneCanvas> = { webglOk: false };
const REMOUNTED: Partial<IStateSceneCanvas> = { webglOk: true };

/**
 * THE 3D SCENE'S CANVAS (frontend-framework-oo P4, 2026-10-29, §28.296) — `GameScene.tsx`'s state and its recovery effect.
 * Backgrounding a tab, or GPU memory pressure from other heavy tabs, can drop the WebGL context: the canvas's handler switches to the
 * 2D cup. The lost context cannot be revived on the unmounted canvas, so when the tab returns to the foreground a FRESH canvas is
 * mounted; if GPU pressure loses that one again almost at once, a full reload is the reliable cure (identity is durable, so the player
 * lands straight back in the game). Only an actual loss is retried — never a deliberate `?force2d` or a missing WebGL2.
 */
export class SceneCanvasViewModel implements IServiceViewModel<IStateSceneCanvas> {
  private readonly state: ObservableState<IStateSceneCanvas>;
  private readonly reactions = new Reactions<null>([{
    deps: () => [],
    run: () => (this.deps.webglAvailable ? this.deps.platform.visibility.onReturn(this.recover) : undefined),
  }]);

  constructor(private readonly deps: IConfigDepSceneCanvas) {
    const initial: IStateSceneCanvas = {
      webglOk: deps.webglAvailable, gldebug: new URLSearchParams(deps.platform.address.search()).has('gldebug'),
    };
    this.state = new ObservableState<IStateSceneCanvas>(initial);
  }

  get runtime(): SceneRuntime {
    return this.deps.runtime;
  }

  /** Back in front: a lost context is retried on a fresh canvas — and reloaded if it is lost again within the grace. */
  private readonly recover = (): void => {
    const { visibility, timers, reload } = this.deps.platform;
    const runtime = this.deps.runtime;
    if (!visibility.visible()) return;
    if (!runtime.contextLost) return;
    runtime.contextLost = false;
    this.state.update(REMOUNTED);
    timers.setTimeout(() => {
      if (visibility.visible() && runtime.contextLost) reload();
    }, REMOUNT_GRACE_MS);
  };

  /** The canvas made: counted, and its context's loss watched — the loss switches the scene to the 2D cup. */
  readonly canvasCreated = (context: IEntityCanvasContext): void => {
    this.deps.runtime.created = true;
    context.canvas.addEventListener('webglcontextlost', (event) => {
      event.preventDefault();
      this.deps.runtime.contextLost = true;
      this.state.update(LOST);
    }, false);
  };

  getSnapshot(): IStateSceneCanvas {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(): void {
    this.reactions.stopChanged(null);
  }

  commit(): void {
    this.reactions.startChanged(null);
  }
}
